import { useId, useState, type ReactNode } from 'react';
import type {
  LoiOutcome,
  LoiOutcomeItem,
  SellerDecision,
  SellerVoteChoice,
  SellerVoteLine,
  TeamRole,
} from '../api/types';
import { TEAM_ROLES, TEAM_ROLE_LABELS } from '../api/types';
import { formatDate, formatDateOnly } from '../lib/date-only';
import { cn } from '../lib/utils';
import { Button } from './button';
import { DecisionConfirmDialog, voteCopy } from './decision-confirm';
import { LegendWithAside } from './legend-aside';
import { Chip, type ChipTone } from './status-chip';

/**
 * The OWNERS' VOTE and TEAM ROLES UI (ADR-0028 / ADR-0046 amendment, owner
 * decisions 2026-10-22), shared by the seller portal and the admin app so both
 * describe the same decision the same way. Presentational — pages own the
 * mutations. (The people with ownership are "Owners" everywhere people read; the
 * code keeps the word "seller".)
 *
 *   Only the OWNERS decide anything binding on the company, by an
 *   ownership-weighted majority of the whole company: a decision is approved the
 *   moment the Approve ownership is MORE than 50%, and declined when the Decline
 *   ownership reaches 50% — so exactly 50/50 is never an approval. The
 *   intermediary only ever PROPOSES. A seller's-team member never sees a vote, let
 *   alone casts one. Every Approve / Decline click asks "Are you sure?" first.
 */

/** "60%", "33.333%" — no trailing zeros. */
export function formatPct(pct: number): string {
  return `${Number(pct.toFixed(3))}%`;
}

/**
 * The plain warning shown (admin and owners) while the owners in the system hold
 * 50% or less together, so nothing can be approved; null once they hold more. The
 * server serves the same sentence (`warning`).
 */
export function ownershipLowWarning(totalPct: number): string | null {
  return Math.round(totalPct * 1000) > 50_000
    ? null
    : `Owners in the system hold ${formatPct(totalPct)} — nothing can be approved until owners holding more than 50% are added.`;
}

/**
 * Whether the room's OWNERS may START an LOI decision or an extension (owner
 * ruling 2026-10-22): in a room the intermediary runs, only while the room's
 * "let the owners respond" toggle is on; in a room the buyer or the seller runs
 * the owners are the only deciders, whatever the toggle says. (Voting on what the
 * intermediary proposed is always theirs.)
 */
export function ownersMayStartLoiDecision(room: {
  adminRole?: 'intermediary' | 'buyer' | 'seller' | null;
  sellerCanRespondLoi?: boolean;
}): boolean {
  // A legacy room with no role is intermediary-run.
  const runBy = room.adminRole ?? 'intermediary';
  return runBy !== 'intermediary' || room.sellerCanRespondLoi !== false;
}

/** What approving this decision means, in plain words. */
export function approveMeaning(
  d: Pick<SellerDecision, 'action' | 'buyerEmail' | 'lengthDays' | 'startDate' | 'additionalDays'>,
): string {
  const buyer = d.buyerEmail ?? 'the buyer';
  switch (d.action) {
    case 'accept':
      return (
        `Accept the letter of intent from ${buyer}` +
        (d.lengthDays != null && d.startDate
          ? ` and grant them ${d.lengthDays} days of exclusivity from ${formatDateOnly(d.startDate)}`
          : '') +
        '. Every other buyer’s access is paused for the exclusivity period.'
      );
    case 'counter':
      return `Counter the letter of intent from ${buyer}: send it back with your changes.`;
    case 'reject':
      return `Decline the letter of intent from ${buyer}.`;
    case 'extend':
      return `Extend ${buyer}’s exclusivity by ${d.additionalDays ?? '…'} days.`;
  }
}

/** What declining this decision means, in plain words. */
export function declineMeaning(action: SellerDecision['action']): string {
  return action === 'accept'
    ? 'Vote against it. If owners holding half or more of the company decline, the acceptance is dropped and the letter of intent is open for a response again.'
    : action === 'extend'
      ? 'Vote against it. If owners holding half or more of the company decline, the extension is dropped.'
      : 'Vote against it. If owners holding half or more of the company decline, the proposal is dropped and nothing is recorded.';
}

const VOTE_WORD: Record<SellerVoteChoice, string> = { approve: 'Approved', decline: 'Declined' };

function voteTone(vote: SellerVoteChoice | null): ChipTone {
  return vote === 'approve' ? 'certified' : vote === 'decline' ? 'error' : 'neutral';
}

function sellerLabel(s: Pick<SellerVoteLine, 'displayName' | 'email' | 'actorId'>): string {
  return s.displayName && s.email ? `${s.displayName} (${s.email})` : (s.email ?? s.actorId);
}

/** A one-line summary of a decision's tally, e.g. "40% approved · 0% declined of 100%". */
export function SellerVoteSummary({ decision }: { decision: SellerDecision }) {
  return (
    <span data-testid="seller-vote-summary">
      {formatPct(decision.approvedPct)} approved · {formatPct(decision.declinedPct)} declined of{' '}
      {formatPct(decision.totalPct)}
    </span>
  );
}

/**
 * One decision waiting for the owners: what it is (and what the intermediary
 * proposed, when it did), who has voted and how much of the company has approved /
 * declined, and — for an owner — Approve / Decline, each behind an "Are you sure?"
 * (changeable until it completes). The admin sees the same tally read-only.
 */
export function SellerVotePanel({
  decision,
  pending = false,
  error,
  onVote,
  title,
  titleAside,
}: {
  decision: SellerDecision;
  pending?: boolean;
  error?: string | null;
  onVote?: (vote: SellerVoteChoice) => void;
  title?: string;
  /** A "?" help tip shown after the heading. */
  titleAside?: ReactNode;
}) {
  const headingId = useId();
  const [confirming, setConfirming] = useState<SellerVoteChoice | null>(null);
  const heading =
    title ??
    (decision.action === 'accept'
      ? 'Accept this letter of intent?'
      : decision.action === 'counter'
        ? 'Counter this letter of intent?'
        : decision.action === 'reject'
          ? 'Decline this letter of intent?'
          : 'Extend the exclusivity period?');
  const who =
    decision.proposedByRole === 'intermediary'
      ? 'The intermediary proposed this'
      : 'An owner proposed this';
  return (
    <section
      aria-labelledby={headingId}
      data-testid="seller-vote"
      className="space-y-3 rounded-md border border-st-check-line bg-st-check p-4 text-st-check-fg"
    >
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <h3 id={headingId} className="text-sm font-semibold">
            {heading}
          </h3>
          {titleAside}
        </div>
        <p className="text-sm">
          {who}. {approveMeaning(decision)} It is decided by the owners’ ownership-weighted vote:
          approved as soon as owners holding <strong>more than 50%</strong> of the company approve,
          dropped when owners holding <strong>50% or more</strong> decline.
        </p>
      </div>

      {!decision.canApprove ? (
        <p
          role="alert"
          data-testid="vote-low-ownership"
          className="text-sm font-medium text-destructive"
        >
          {decision.warning ??
            `Owners in the system hold ${formatPct(decision.totalPct)} — nothing can be approved until owners holding more than 50% are added.`}
        </p>
      ) : null}

      <div className="space-y-1.5">
        <p className="text-sm font-medium" aria-live="polite">
          <SellerVoteSummary decision={decision} />
        </p>
        <div
          className="flex h-2 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={`${formatPct(decision.approvedPct)} approved, ${formatPct(decision.declinedPct)} declined`}
        >
          <div className="bg-st-certified-fg" style={{ width: `${decision.approvedPct}%` }} />
          <div className="bg-destructive" style={{ width: `${decision.declinedPct}%` }} />
        </div>
        <ul className="divide-y text-sm" aria-label="How each owner voted">
          {decision.sellers.map((s) => (
            <li
              key={s.actorId}
              className="flex flex-wrap items-center justify-between gap-2 py-1.5"
            >
              <span>
                {sellerLabel(s)}{' '}
                <span className="text-muted-foreground">· {formatPct(s.ownershipPct)}</span>
              </span>
              {s.vote ? (
                <Chip tone={voteTone(s.vote)}>{VOTE_WORD[s.vote]}</Chip>
              ) : (
                <span className="text-xs text-muted-foreground">Not voted yet</span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {decision.viewerCanVote && onVote ? (
        <div className="space-y-2">
          {decision.viewerVote ? (
            <p className="text-sm">
              You voted <strong>{decision.viewerVote === 'approve' ? 'Approve' : 'Decline'}</strong>
              . You can change your vote until it is decided.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={pending || decision.viewerVote === 'approve'}
              onClick={() => setConfirming('approve')}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pending || decision.viewerVote === 'decline'}
              onClick={() => setConfirming('decline')}
            >
              Decline
            </Button>
          </div>
          <dl className="space-y-0.5 text-xs text-muted-foreground">
            <div>
              <dt className="inline font-medium text-foreground">Approve — </dt>
              <dd className="inline">{approveMeaning(decision)}</dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">Decline — </dt>
              <dd className="inline">{declineMeaning(decision.action)}</dd>
            </div>
          </dl>
        </div>
      ) : (
        <p className="text-xs">
          {decision.viewerCanVote
            ? ''
            : 'Waiting for the owners to vote. Only the owners decide this.'}
        </p>
      )}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {confirming && onVote ? (
        <DecisionConfirmDialog
          copy={voteCopy(decision, confirming)}
          danger={confirming === 'decline'}
          pending={pending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const vote = confirming;
            setConfirming(null);
            onVote(vote);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * The warning shown (admin and owners) while the owners in the system hold 50% or
 * less together, so nothing can be approved. `warning` is the server's plain
 * sentence ("Owners in the system hold X% — nothing can be approved until owners
 * holding more than 50% are added.").
 */
export function SellerOwnershipWarning({
  warning,
  className,
}: {
  warning: string;
  className?: string;
}) {
  return (
    <div
      role="alert"
      data-testid="seller-ownership-warning"
      className={cn(
        'rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive',
        className,
      )}
    >
      {warning} Add owners or change their percentages on the Access page.
    </div>
  );
}

// ---------------------------------------------------------------------------
// LOI outcome (what a seller's legal team sees)
// ---------------------------------------------------------------------------

const OUTCOME_TONE: Record<LoiOutcome, ChipTone> = {
  Approved: 'certified',
  Declined: 'error',
  Redlined: 'recert',
  Pending: 'check',
};

export function LoiOutcomeChip({ outcome }: { outcome: LoiOutcome }) {
  return <Chip tone={OUTCOME_TONE[outcome]}>{outcome}</Chip>;
}

/**
 * The LOIs as a seller's LEGAL team member sees them: the buyer, the date, the
 * signed document (read-only) and the FINAL OUTCOME — Approved, Declined,
 * Redlined or Pending. No votes, no controls: only the sellers decide.
 */
export function LoiOutcomeTable({
  lois,
  onOpen,
}: {
  lois: readonly LoiOutcomeItem[];
  onOpen: (loi: LoiOutcomeItem) => void;
}) {
  return (
    <div className="space-y-3">
      <p role="note" className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
        You can read the letters of intent and see how each stands. Only the owners decide them.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Buyer</th>
              <th className="py-2 pr-4 font-medium">Submitted</th>
              <th className="py-2 pr-4 font-medium">Outcome</th>
              <th className="py-2 font-medium">Letter of intent</th>
            </tr>
          </thead>
          <tbody>
            {lois.map((loi) => (
              <tr key={loi.id} className="border-b align-top last:border-0">
                <td className="py-2 pr-4">{loi.actorEmail ?? 'A buyer'}</td>
                <td className="py-2 pr-4 whitespace-nowrap">{formatDate(loi.submittedAt)}</td>
                <td className="py-2 pr-4">
                  <LoiOutcomeChip outcome={loi.outcome} />
                  {loi.decidedAt ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatDate(loi.decidedAt)}
                    </div>
                  ) : null}
                </td>
                <td className="py-2">
                  {loi.documentId ? (
                    <button
                      type="button"
                      onClick={() => onOpen(loi)}
                      className="max-w-[14rem] truncate text-left text-xs font-medium text-primary hover:underline"
                      title={loi.filename ?? undefined}
                    >
                      {loi.filename ?? 'View document'}
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No document</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** True for the Legal-view item shape (outcome only, no status detail). */
export function isLoiOutcomeItem(item: {
  outcome?: LoiOutcome;
  status?: unknown;
}): item is LoiOutcomeItem {
  return item.status === undefined && item.outcome !== undefined;
}

// ---------------------------------------------------------------------------
// Team roles
// ---------------------------------------------------------------------------

/** Small chips beside a team member: Legal, Financial, … (nothing when none). */
export function TeamRoleChips({
  roles,
  className,
}: {
  roles: readonly TeamRole[] | null | undefined;
  className?: string;
}) {
  if (!roles || roles.length === 0) return null;
  return (
    <span className={cn('inline-flex flex-wrap gap-1', className)} data-testid="team-role-chips">
      {roles.map((r) => (
        <span
          key={r}
          className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[11px] font-medium text-ink-2"
        >
          {TEAM_ROLE_LABELS[r]}
        </span>
      ))}
    </span>
  );
}

/** Pick any number of team roles (checkboxes). Controlled. */
export function TeamRolePicker({
  value,
  onChange,
  disabled = false,
  legend = 'Roles',
  legendAside,
  legalNote = true,
  className,
}: {
  value: readonly TeamRole[];
  onChange: (roles: TeamRole[]) => void;
  disabled?: boolean;
  legend?: string;
  /** A "?" help tip shown after the legend (outside it, so the group name is unchanged). */
  legendAside?: ReactNode;
  /** Say what the Legal role does (only meaningful on a seller's team). */
  legalNote?: boolean;
  className?: string;
}) {
  return (
    <fieldset className={cn('space-y-1.5', className)} disabled={disabled}>
      <LegendWithAside className="text-sm font-medium" aside={legendAside}>
        {legend}
      </LegendWithAside>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {TEAM_ROLES.map((role) => (
          <label key={role} className="inline-flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={value.includes(role)}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? TEAM_ROLES.filter((r) => r === role || value.includes(r))
                    : value.filter((r) => r !== role),
                )
              }
            />
            {TEAM_ROLE_LABELS[role]}
          </label>
        ))}
      </div>
      {legalNote ? (
        <p className="text-xs text-muted-foreground">
          Only “Legal” has an effect today: a seller’s-team member tagged Legal can read the letters
          of intent and their outcome.
        </p>
      ) : null}
    </fieldset>
  );
}
